import styles from "@/styles/buying-guide/index.module.scss";
import Head from "next/head";
import { useEffect, useState } from "react";
import DefaultLayout from "@/components/DefaultLayout";
import BreadCrum from "@/components/global/BreadCrum";
import GuideHeader from "@/components/buying-guide/GuideHeader";
import PopularBuyingGuides from "@/components/buying-guide/PopularBuyingGuides";
import Link from "next/link";
import Image from "next/image";
import buyingGuidesApi from "@/service/modules/buyingGuidesApi";

import getConfig from "next/config";
const { publicRuntimeConfig } = getConfig();
const imageDomain = publicRuntimeConfig.imageDomain;

export default function BuyingGuides() {
  interface BreadcrumbItem {
    name: string;
    url: string;
    current: boolean;
  }

  interface ReviewItemType {
    title: string;
    description: string;
    url: string;
  }

  interface BlogItemType {
    title: string;
    description: string;
    url: string;
  }

  interface PopularBuyingGuidesItem {
    categoryID?: number;
    name: string;
    guideUrl: string;
    imgUrl: string;
    seoDescription: string | null;
  }

  const [breadCrumb, setBreadCrumb] = useState<BreadcrumbItem[]>([]);
  const [guideData, setGuideData] = useState<Record<string, any>>({});
  const [popularBuyingGuidesData, setPopularBuyingGuidesData] = useState<PopularBuyingGuidesItem[][]>([]);
  const [reviewData, setReviewData] = useState<ReviewItemType[]>([]);
  const [blogData, setBlogData] = useState<BlogItemType[]>([]);

  // 将数据分类，商品数组拆分为多个页面（每个page 8条数据）
  function paginateProducts(products: PopularBuyingGuidesItem[],itemsPerPage: number): PopularBuyingGuidesItem[][] {
    const paginatedArray: PopularBuyingGuidesItem[][] = [];
    const totalPages = Math.ceil(products.length / itemsPerPage);

    for (let i = 0; i < totalPages; i++) {
      const startIndex = i * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const page = products.slice(startIndex, endIndex);
      paginatedArray.push(page);
    }

    return paginatedArray;
  }

  // 获取Guide模块数据
  async function getpopularBuyingGuidesData() {
    try {
      let resData = await buyingGuidesApi.getBuyingGuides();

      if (resData.data && resData.data && resData.data.guideBox.length) {
        resData.data.guideBox.forEach((item: any) => {
          item.guideUrl = `/${item.guideUrl}`;
        });
        // 将每个页面包含 8 个商品的数组
        const paginatedProducts = paginateProducts(resData.data.guideBox, 8);

        setPopularBuyingGuidesData(paginatedProducts);
        setGuideData(resData.data.guide);
        setReviewData(resData.data.guide.review);
        setBlogData(resData.data.guide.blog);
      }
    } catch (error) {
      console.log(`获取Guide模块数据失败: `, error);
    }
  }

  useEffect(() => {
    getpopularBuyingGuidesData();
  }, []);

  useEffect(() => {
    if (guideData.guideUrl) {
      setBreadCrumb([
        {
          name: "Buying Guides",
          url: "/buying-guides/",
          current: true,
        },
      ]);
    }
  }, [guideData]);

  return (
    <>
      <Head>
        <title>
          Online Shopping Australia - Beauty & Health, Computers, Toys, - Deals
          & Discounts | CrazySales
        </title>
        <meta
          name="keywords"
          content="Online Shopping Australia, online shopping sites, Home Shopping, Shopping Deals, Best Deals, Home Garden, Pet Supplies, Sports Fitness, Bedding Bath, Buy Online, Discount Shopping, Toys Hobbies, Beauty Health, Crazy Sales, Computers, IT, Christmas, Electronics, Automotive, Tools, Appliances, Baby, CrazySales"
        ></meta>
        <meta
          name="description"
          content="CrazySales.com.au - Australia's #1 online shopping website! Cheap bargains &amp; discounts with the top online shopping Australia store! From furniture, to toys &amp; hobbies, to appliances &amp; pet supplies -- we've got it all, with free shipping on selected items!"
        ></meta>
      </Head>

      <DefaultLayout notStickyNavbar={true}>
        <div className={`${styles["page-buying-guides"]}`}>
          {/* 面包屑 */}
          {!!(breadCrumb && breadCrumb.length) && (
            <BreadCrum BreadList={breadCrumb}></BreadCrum>
          )}

          <div className={styles["content"]}>
            <GuideHeader
              seoDescription={guideData.seoDescription}
            ></GuideHeader>

            <div className={styles["guides-title"]}>Popular Buying Guides</div>

            {!!(popularBuyingGuidesData && popularBuyingGuidesData.length) && (
              <PopularBuyingGuides
                PopularBuyingGuidesData={popularBuyingGuidesData}
              ></PopularBuyingGuides>
            )}

            <div className={styles["reviews-and-articles"]}>
              <div className={styles["reviews-box"]}>
                <div className={styles["reviews-title"]}>Reviews</div>

                <div className={styles["reviews-list"]}>
                  {reviewData.map((item: ReviewItemType) => {
                    return (
                      <Link href={item.url} legacyBehavior key={item.title}>
                        <a
                          className={styles["reviews-item"]}
                          rel="noopener noreferrer"
                        >
                          <Image
                            className={styles["item-icon"]}
                            src={`${imageDomain}/svg/model-photos.svg`}
                            alt={item.title}
                            width={54}
                            height={54}
                          />

                          <div className={styles["item-text-box"]}>
                            <div className={styles["item-text-title"]}>
                              {item.title}
                            </div>
                            <div className={styles["item-text-desc"]}>
                              {item.description}
                            </div>
                          </div>
                        </a>
                      </Link>
                    );
                  })}
                </div>
              </div>
              <div className={styles["articles-box"]}>
                <div className={styles["articles-title"]}>Articles</div>

                <div className={styles["articles-list"]}>
                  {blogData.map((item: BlogItemType) => {
                    return (
                      <Link href={item.url} legacyBehavior key={item.title}>
                        <a
                          className={styles["articles-item"]}
                          rel="noopener noreferrer"
                        >
                          <Image
                            className={styles["item-icon"]}
                            src={`${imageDomain}/legal/articles-icon.webp`}
                            alt={""}
                            width={36}
                            height={34}
                          />

                          <div className={styles["item-text-box"]}>
                            <div className={styles["item-text-title"]}>
                              {item.title}
                            </div>
                            <div className={styles["item-text-desc"]}>
                              {item.description}
                            </div>
                          </div>
                        </a>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DefaultLayout>
    </>
  );
}
